import { useEffect } from "react";
import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/api";
import { useUserMe } from "@/lib/useUserMe";
import { useFollows } from "@/lib/useFollows";
import { useSavedIntelligenceIds } from "@/lib/useSavedIntelligenceIds";
import { useTrack } from "@/lib/track";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/intel/Section";
import { ItemCard, ItemCardEmpty, type FeedItem } from "@/components/intel/ItemCard";
import { BriefingRenderer } from "@/components/intel/BriefingRenderer";
import { EveningReflection } from "@/components/intel/EveningReflection";
import { MemoryLayer } from "@/components/intel/MemoryLayer";
import { MiddayMoment } from "@/components/intel/MiddayMoment";
import { JourneyPanel } from "@/components/intel/JourneyPanel";
import { useMorningFocus } from "@/lib/useRhythm";
import {
  OpportunityCard,
  type Opportunity,
} from "@/components/opps/OpportunityCard";

interface Briefing {
  id: string;
  title: string;
  body_md: string | null;
  body_json: string | null;
  generated_at: number;
}


type Emphasis = "trend" | "opportunity" | "leadership" | null;

export function Dashboard() {
  const api = useApi();
  const me = useUserMe();
  const follows = useFollows();
  const savedIds = useSavedIntelligenceIds();
  const track = useTrack();
  const userRegion = me.data?.user?.region ?? null;
  const displayName = me.data?.user?.display_name ?? null;
  const hasFollows = (follows.data?.follows.length ?? 0) > 0;

  useEffect(() => {
    track("dashboard.view");
  }, [track]);

  const todayBrief = useQuery({
    queryKey: ["briefing-today"],
    queryFn: () => api.get<{ briefing: Briefing | null }>("/briefings/today"),
  });

  const generateBrief = useMutation({
    mutationFn: () =>
      api.post<{ briefing: Briefing; cached?: boolean }>("/briefings/generate", {
        kind: "daily",
        windowHours: 24,
      }),
    onSuccess: (data) => {
      track("brief.generate", { cached: Boolean(data.cached) });
      todayBrief.refetch();
    },
  });

  // Auto-generate today's brief on first load if none exists. Server-side
  // idempotency on /briefings/generate prevents duplicate OpenAI calls if
  // this fires twice (StrictMode dev double-invoke).
  const needsBrief =
    todayBrief.isSuccess && !todayBrief.data?.briefing && generateBrief.isIdle;
  useEffect(() => {
    if (needsBrief) generateBrief.mutate();
  }, [needsBrief, generateBrief]);

  const brief = generateBrief.data?.briefing ?? todayBrief.data?.briefing ?? null;

  const forYouFeed = useQuery({
    queryKey: ["feed", "for_you"],
    queryFn: () =>
      api.get<{ items: FeedItem[] }>("/intelligence/feed?lens=for_you&limit=4"),
    enabled: hasFollows,
  });
  const topFeed = useQuery({
    queryKey: ["feed", "top"],
    queryFn: () =>
      api.get<{ items: FeedItem[] }>("/intelligence/feed?lens=top&limit=5"),
  });
  const trendFeed = useQuery({
    queryKey: ["feed", "trending"],
    queryFn: () =>
      api.get<{ items: FeedItem[] }>("/intelligence/feed?lens=trending&limit=4"),
  });
  const oppsPreview = useQuery({
    queryKey: ["opportunities", "preview"],
    queryFn: () =>
      api.get<{ opportunities: Opportunity[] }>("/opportunities?lens=active&limit=3"),
  });
  const localFeed = useQuery({
    queryKey: ["feed", "local", userRegion],
    queryFn: () =>
      api.get<{ items: FeedItem[] }>(
        `/intelligence/feed?lens=top&geo=${encodeURIComponent(userRegion!)}&limit=4`,
      ),
    enabled: !!userRegion,
  });
  const leadFeed = useQuery({
    queryKey: ["feed", "leadership"],
    queryFn: () =>
      api.get<{ items: FeedItem[] }>("/intelligence/feed?lens=leadership&limit=3"),
  });

  const evening = isEvening();
  const morning = isMorning();
  const midday = isMidday();
  const morningFocus = useMorningFocus(morning);

  return (
    <div className="px-10 py-10 max-w-6xl">
      <header className="mb-10">
        <p className="label-eyebrow">{greeting()}</p>
        <h1 className="mt-2 text-display-2 font-display">
          {displayName
            ? `${evening ? "Tonight" : morning ? "This morning" : "Welcome"}, ${displayName}`
            : "Intelligence"}
        </h1>
        <p className="mt-2 text-whisper text-sm">{todayLabel()}</p>
        {morning && morningFocus.data?.focus && (
          <p className="mt-3 text-sm text-fog/90 italic leading-relaxed max-w-2xl">
            {morningFocus.data.focus}
          </p>
        )}
      </header>

      <JourneyPanel />

      {midday && <MiddayMoment />}

      {evening ? (
        <Section
          eyebrow="Tonight"
          title="Evening reflection"
          detail="A private prompt for closing the day. Today's morning brief is in Briefings."
        >
          <EveningReflection />
        </Section>
      ) : (
        <Section
        eyebrow="Morning Brief"
        title={brief?.title ?? "Today's read"}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateBrief.mutate()}
            disabled={generateBrief.isPending}
          >
            {generateBrief.isPending ? "Synthesizing…" : "Regenerate"}
          </Button>
        }
      >
        {brief ? (
          <article className="panel p-8 animate-fade-up">
            <BriefingRenderer body_json={brief.body_json} body_md={brief.body_md} />
          </article>
        ) : todayBrief.isLoading || generateBrief.isPending ? (
          <ItemCardEmpty label="Reading the day's signals…" />
        ) : (
          <ItemCardEmpty
            label="No brief yet"
            detail="Once enough signals are synthesized, your daily brief will appear here."
          />
        )}
      </Section>
      )}

      <MemoryLayer />

      {hasFollows && (
        <Section
          eyebrow="For You"
          title="From your follows"
          detail="Signals matching the topics and geographies you follow."
        >
          <FeedList
            feed={forYouFeed}
            empty="No matches yet from your follows."
            emphasis={null}
            savedIds={savedIds.data}
            withExplanations
            userRegion={userRegion}
          />
        </Section>
      )}

      <Section eyebrow="Top Signals" title="What matters most">
        <FeedList feed={topFeed} empty="No top signals yet." emphasis={null} savedIds={savedIds.data} />
      </Section>

      <Section eyebrow="Trending" title="Fast-moving stories">
        <FeedList feed={trendFeed} empty="No trending stories yet." emphasis="trend" savedIds={savedIds.data} />
      </Section>

      <Section
        eyebrow="Opportunities"
        title="Active openings"
        action={
          <Link
            to="/app/opportunities"
            className="text-xs uppercase tracking-[0.18em] text-whisper hover:text-gold-glow"
          >
            View all →
          </Link>
        }
      >
        <OppPreview
          opps={oppsPreview.data?.opportunities ?? []}
          loading={oppsPreview.isLoading}
        />
      </Section>

      {userRegion && (
        <Section
          eyebrow="Local"
          title={`Signals from ${userRegion}`}
          detail="Filtered by your home region."
        >
          <FeedList
            feed={localFeed}
            empty={`No local signals from ${userRegion} yet.`}
            emphasis={null}
            savedIds={savedIds.data}
          />
        </Section>
      )}

      <Section
        eyebrow="Leadership Insight"
        title="For operators"
        detail="Items rated high in leadership relevance by the synthesis layer."
      >
        <FeedList
          feed={leadFeed}
          empty="No leadership-relevant items yet."
          emphasis="leadership"
          savedIds={savedIds.data}
        />
      </Section>

      <footer className="mt-16 border-t border-veil/30 pt-6">
        <Link
          to="/app/rhythm/weekly"
          className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-gold-glow transition-colors"
        >
          Weekly reset →
        </Link>
      </footer>
    </div>
  );
}

function FeedList({
  feed,
  empty,
  emphasis,
  savedIds,
  withExplanations = false,
  userRegion = null,
}: {
  feed: UseQueryResult<{ items: FeedItem[] }>;
  empty: string;
  emphasis: Emphasis;
  savedIds?: Set<string>;
  withExplanations?: boolean;
  userRegion?: string | null;
}) {
  if (feed.isLoading) return <ItemCardEmpty label="Reading signals…" />;
  if (feed.isError) return <ItemCardEmpty label="Couldn't load this section." />;
  if (!feed.data || feed.data.items.length === 0) {
    return <ItemCardEmpty label={empty} />;
  }
  return (
    <div className="grid gap-4">
      {feed.data.items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          emphasis={emphasis}
          isSaved={savedIds?.has(item.id)}
          explanation={withExplanations ? explanationFor(item, userRegion) : null}
        />
      ))}
    </div>
  );
}

/**
 * Build a one-line explanation for a for_you item. Priority:
 *   1. matched category → "Because you follow {category}"
 *   2. matched geo that the user explicitly follows → "Because you follow {geo}"
 *   3. matched geo that equals the user's home region → "Relevant to {region}"
 *   4. nothing matched → null (the card renders no eyebrow)
 *
 * Never returns more than one reason — over-explanation defeats the purpose.
 */
function explanationFor(item: FeedItem, userRegion: string | null): string | null {
  if (item.match_category) return `Because you follow ${item.match_category}`;
  if (item.match_geo) {
    if (userRegion && item.match_geo === userRegion) return `Relevant to ${item.match_geo}`;
    return `Because you follow ${item.match_geo}`;
  }
  return null;
}

function OppPreview({ opps, loading }: { opps: Opportunity[]; loading: boolean }) {
  if (loading) return <ItemCardEmpty label="Reading opportunities…" />;
  if (opps.length === 0) return <ItemCardEmpty label="No active opportunities yet." />;
  return (
    <div className="grid gap-3">
      {opps.map((o) => (
        <OpportunityCard key={o.id} opp={o} variant="compact" />
      ))}
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "Late night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Late night";
}

/**
 * Evening swap window: 5pm–4:59am local. Outside this window the dashboard
 * leads with the morning brief; inside it, the lead surface becomes the
 * evening reflection prompt. Morning brief stays generated and is reachable
 * via Briefings.
 */
function isEvening(): boolean {
  const h = new Date().getHours();
  return h >= 17 || h < 5;
}

/**
 * Morning window: 5am–11:59am local. Only when in morning do we fetch
 * the "focus for today" line — keeps the request quiet outside its window.
 */
function isMorning(): boolean {
  const h = new Date().getHours();
  return h >= 5 && h < 12;
}

/**
 * Midday window: 12pm–2:59pm local. Only when in midday does the
 * MiddayMoment render. Outside the window it returns null even if data is
 * cached.
 */
function isMidday(): boolean {
  const h = new Date().getHours();
  return h >= 12 && h < 15;
}

function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}
